/**
 * BrandDeckPanel — Project-level brand reference asset uploader.
 *
 * Shows all uploaded brand deck pages (PDF pages or individual images) with
 * captions, lets the user upload more (drag-drop or click), and click any page
 * to link/unlink it to a specific costume in the CCC list.
 *
 * Usage:
 *   <BrandDeckPanel
 *     projectId={projectId}
 *     costumes={derivedCCC?.costumes || []}
 *     onLinked={() => reloadProjectData()}
 *   />
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, UploadCloud, Trash2, X, Check, Plus, Link2 } from 'lucide-react';
import { toast } from '../utils/toast';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrandDeckPanel({ projectId, costumes = [], onLinked }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [linking, setLinking] = useState(null); // { deckId, deckUrl } when picking a costume
  const [previewPage, setPreviewPage] = useState(null);
  const fileInputRef = useRef(null);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/brand-deck`);
      setPages(res.data.items || []);
    } catch (err) {
      console.error('Failed to load brand deck:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPages(); /* eslint-disable-next-line */ }, [projectId]);

  const linkedByDeckId = useMemo(() => {
    const map = new Map();
    (costumes || []).forEach(c => {
      if (c.brand_deck_ref_id) {
        if (!map.has(c.brand_deck_ref_id)) map.set(c.brand_deck_ref_id, []);
        map.get(c.brand_deck_ref_id).push(c.character_name);
      }
    });
    return map;
  }, [costumes]);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append('files', f));
      const res = await axios.post(`${API}/projects/${projectId}/brand-deck/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 240000,
      });
      const added = res.data?.added || 0;
      toast.success(`Added ${added} brand deck page${added === 1 ? '' : 's'} — auto-captioning…`);
      await fetchPages();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Upload failed: ${msg}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) handleFiles(files);
  };

  const handleDelete = async (deckId) => {
    if (!confirm('Remove this brand deck page? Any costumes linked to it will be unlinked.')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/brand-deck/${deckId}`);
      toast.success('Page removed');
      await fetchPages();
      onLinked?.();
    } catch (err) {
      toast.error(`Delete failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleLink = async (deckId, characterName) => {
    try {
      await axios.post(`${API}/projects/${projectId}/brand-deck/${deckId}/link`, {
        costume_character_name: characterName,
      });
      toast.success(`Linked to ${characterName}'s costume`);
      setLinking(null);
      onLinked?.();
    } catch (err) {
      toast.error(`Link failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleUnlink = async (characterName) => {
    try {
      await axios.post(`${API}/projects/${projectId}/brand-deck/unlink`, {
        costume_character_name: characterName,
      });
      toast.success(`Unlinked from ${characterName}`);
      onLinked?.();
    } catch (err) {
      toast.error(`Unlink failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  // Re-run flat-lay refinement on every costume that is currently linked to a
  // brand deck page. The backend treats each linked deck PNG as the source
  // image and replaces the costume_candidates URL with the flat-lay output.
  const [regenerating, setRegenerating] = useState(false);
  const handleRegenerateLinked = async () => {
    const linkedCount = (costumes || []).filter(c => c.brand_deck_ref_id).length;
    if (linkedCount === 0) {
      toast.error('No costumes are linked to brand deck pages yet');
      return;
    }
    if (!confirm(`Re-run costume generation for ${linkedCount} linked costume${linkedCount === 1 ? '' : 's'}? Each will use its linked brand deck page as the source.`)) return;
    setRegenerating(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/brand-deck/regenerate-linked`);
      const queued = res.data?.queued || 0;
      if (queued === 0) {
        toast.error(res.data?.message || 'No costumes queued — re-derive CCC first');
      } else {
        toast.success(`Re-running ${queued} linked costume${queued === 1 ? '' : 's'} — this may take a couple of minutes…`);
      }
      // Hint the parent to refresh on a delay, then again so the user sees the
      // updated thumbnails as the background tasks finish.
      setTimeout(() => onLinked?.(), 8000);
      setTimeout(() => onLinked?.(), 25000);
    } catch (err) {
      toast.error(`Re-run failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setRegenerating(false);
    }
  };

  const resolveUrl = (u) => (u && u.startsWith('http')) ? u : `${process.env.REACT_APP_BACKEND_URL}${u || ''}`;

  return (
    <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]" data-testid="brand-deck-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <UploadCloud className="w-3.5 h-3.5 text-[#0EA5E9]" />
          <h3 className="text-xs font-semibold text-white">Brand Deck</h3>
          <span className="text-[10px] text-white/40">{pages.length} page{pages.length === 1 ? '' : 's'}</span>
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="px-2.5 py-1 rounded-md text-[10px] font-medium bg-[#0EA5E9]/15 text-[#0EA5E9] hover:bg-[#0EA5E9]/25 disabled:opacity-40 flex items-center gap-1"
          data-testid="brand-deck-upload-btn"
        >
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          {uploading ? 'Uploading…' : 'Upload PDF / Images'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg,.webp,.heic"
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
          data-testid="brand-deck-file-input"
        />
      </div>

      {/* Drop zone (when empty) */}
      {pages.length === 0 && !loading && (
        <div
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className="flex flex-col items-center justify-center py-8 px-4 rounded-lg border-2 border-dashed border-white/10 hover:border-[#0EA5E9]/40 hover:bg-[#0EA5E9]/5 cursor-pointer transition-colors"
          data-testid="brand-deck-drop-zone"
        >
          <UploadCloud className="w-7 h-7 text-white/30 mb-2" />
          <p className="text-xs text-white/50 mb-1">Drop a brand deck PDF or images here</p>
          <p className="text-[10px] text-white/30">PDFs are split into pages automatically. Each page gets an AI caption.</p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-4 h-4 animate-spin text-white/40" />
        </div>
      )}

      {/* Page grid */}
      {pages.length > 0 && (
        <div
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDrop={handleDrop}
          className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2"
        >
          {pages.map(page => {
            const linkedNames = linkedByDeckId.get(page.id) || [];
            const isLinking = linking?.deckId === page.id;
            return (
              <div
                key={page.id}
                className={`relative rounded-lg overflow-hidden border transition-all ${
                  isLinking ? 'border-[#22C55E] ring-2 ring-[#22C55E]/40' :
                  linkedNames.length > 0 ? 'border-[#22C55E]/40' : 'border-white/10 hover:border-white/30'
                }`}
                data-testid={`brand-deck-page-${page.id}`}
              >
                <button
                  type="button"
                  onClick={() => setPreviewPage(page)}
                  className="block w-full aspect-[3/4] bg-black/40"
                  title={page.caption || page.original_filename}
                >
                  <img
                    src={resolveUrl(page.url)}
                    alt={page.caption || page.original_filename}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>

                {/* Caption */}
                <div className="px-1.5 py-1 bg-black/70 backdrop-blur-sm">
                  <p className="text-[8px] text-white/70 line-clamp-2" title={page.caption}>
                    {page.caption || <span className="italic text-white/40">No caption yet…</span>}
                  </p>
                  {linkedNames.length > 0 && (
                    <p className="text-[8px] text-[#22C55E] truncate flex items-center gap-0.5 mt-0.5">
                      <Link2 className="w-2 h-2 shrink-0" />
                      {linkedNames.join(', ')}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(page.id); }}
                  className="absolute top-1 right-1 p-0.5 rounded bg-black/60 text-red-300 hover:text-red-400 hover:bg-black/80 opacity-60 hover:opacity-100"
                  title="Delete page"
                  data-testid={`brand-deck-delete-${page.id}`}
                >
                  <Trash2 className="w-2.5 h-2.5" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setLinking(isLinking ? null : { deckId: page.id, deckUrl: page.url }); }}
                  className={`absolute top-1 left-1 p-0.5 rounded text-[8px] font-bold ${
                    isLinking ? 'bg-[#22C55E] text-white' : 'bg-black/60 text-[#22C55E] hover:bg-black/80'
                  } opacity-80 hover:opacity-100`}
                  title={isLinking ? 'Cancel' : 'Link to costume'}
                  data-testid={`brand-deck-link-${page.id}`}
                >
                  {isLinking ? <X className="w-2.5 h-2.5" /> : <Link2 className="w-2.5 h-2.5" />}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Linking picker — show costume list when a deck page is in "linking" mode */}
      {linking && costumes.length > 0 && (
        <div className="mt-3 p-2 rounded-lg bg-[#22C55E]/10 border border-[#22C55E]/30">
          <p className="text-[10px] text-[#22C55E] mb-1.5 font-medium">Pick a costume to link this page to:</p>
          <div className="flex flex-wrap gap-1.5">
            {costumes.map((c, i) => (
              <button
                key={i}
                onClick={() => handleLink(linking.deckId, c.character_name)}
                className="px-2 py-1 rounded text-[10px] bg-[#22C55E]/15 text-[#22C55E] hover:bg-[#22C55E]/30 transition-colors"
                data-testid={`brand-deck-link-target-${i}`}
              >
                {c.character_name}
                {c.brand_deck_ref_id && <span className="ml-1 text-white/50">(replace)</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Inline list of currently linked costumes (for quick unlinking) */}
      {pages.length > 0 && Array.from(linkedByDeckId.values()).flat().length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {(costumes || []).filter(c => c.brand_deck_ref_id).map((c, i) => (
            <button
              key={i}
              onClick={() => handleUnlink(c.character_name)}
              className="px-2 py-0.5 rounded-full text-[9px] bg-[#22C55E]/15 text-[#22C55E] hover:bg-red-500/20 hover:text-red-400 flex items-center gap-1 transition-colors"
              title={`Linked to ${c.brand_deck_ref_caption || 'page'} — click to unlink`}
              data-testid={`brand-deck-unlink-${i}`}
            >
              <Check className="w-2.5 h-2.5" />
              {c.character_name}
            </button>
          ))}
        </div>
      )}

      {/* Lightbox preview */}
      {previewPage && (
        <div
          className="fixed inset-0 z-[9998] flex items-center justify-center p-8 bg-black/90"
          onClick={() => setPreviewPage(null)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setPreviewPage(null)}
              className="absolute -top-2 -right-2 p-1.5 rounded-full bg-white text-black hover:bg-white/90 z-10"
            >
              <X className="w-4 h-4" />
            </button>
            <img
              src={resolveUrl(previewPage.url)}
              alt={previewPage.caption}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <div className="mt-2 px-3 py-2 rounded bg-black/80 text-white">
              <p className="text-xs">{previewPage.caption || <span className="text-white/40 italic">No caption</span>}</p>
              <p className="text-[10px] text-white/40 mt-0.5">{previewPage.original_filename}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
